import { CheckIcon, CopyIcon, PlusIcon, SparkleIcon, TrashIcon } from '@phosphor-icons/react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { UnchangedResendNotice } from './UnchangedResendNotice';
import { isCandidateReadyToPublish, mergePreselectedCandidate } from './postCandidate';

import { formatEventDate } from '@/components/reach/shared/format';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  useApprovalCandidates,
  useCreateApprovalRequest,
  useDraftCandidates,
  useGenerateVariations,
  useUnchangedResends,
} from '@/hooks/approvals/useApprovals';
import { useDeskMembers } from '@/hooks/desk/useDeskMembers';
import { useReachEvents } from '@/hooks/reach/useReachEvents';
import { type ApprovalSnapshotOption, snapshotFromExternalPage } from '@/lib/approvals/snapshot';
import { shareLinkFor } from '@/lib/approvals/token';
import { platformLabel } from '@/lib/publishing/publish-failures';
import { withoutArchivedEvents } from '@/lib/reach/event-archive';
import { FIELD_LABEL, SECTION_LABEL } from '@/lib/ui-tokens';
import type { ApprovalCandidate, NewApprovalItemInput } from '@/services/data/ApprovalService';

/*
 * A single frozen empty array, shared by every render.
 *
 * The point is IDENTITY, not allocation: a fresh `[]` per render is what put an
 * ever-changing value into an effect's dependency array and produced an
 * infinite render loop (see `draftCandidates` below). A module-level constant
 * cannot do that.
 */
const EMPTY_DRAFT_CANDIDATES: never[] = [];

/**
 * The manual asks — things the operator types, as opposed to artifacts they
 * TICK from a candidate list.
 *
 * 🔴 `page` LIVES HERE AND NOT IN A THIRD CANDIDATE LIST, AND THAT WAS
 * MEASURED RATHER THAN PREFERRED.
 *
 * This dialog is already over its own budget: at 1440 with an event selected
 * it needs 1201px of content in a 763px window — 57% overflow — and its two
 * candidate lists disagree about their own height rules (the event-plan list
 * is unbounded, the drafted-posts list is capped at `max-h-52` with its own
 * scroll). A third list-shaped source would add a third disclosure rule and
 * push the manual asks, already last, further down.
 *
 * Adding a KIND here adds no list, no scroll region, and no new decision about
 * where a thing goes: it reuses `addExtra`, the `extras.map` card shell and
 * the same submit path. It is also the family the thing belongs to — a staged
 * page is not a Mora artifact to select, it is something the operator is
 * handing over, like "a yes or no" and "something from them". Founder's own
 * framing, 2026-09-09: "could even add manual actions there which would be
 * staged link for site page or whatever."
 */
type AskKind = 'decision' | 'input' | 'page';

interface ExtraAsk {
  key: string;
  kind: AskKind;
  title: string;
  body: string;
  /** Only ever set for `kind === 'page'`. */
  url?: string;
  /**
   * The day this page is EXPECTED to go live, if the operator knows it. Only
   * meaningful for `kind === 'page'`.
   *
   * 🔴 A PROPOSED DATE, NOT A RELEASE FACT, and it lands in
   * `approval_items.occurs_on` — the column the desk already reads through
   * `deskDateFor` and which `deriveDeskStage` refuses to read as release.
   * Mora does not publish this page and cannot know when it actually ships, so
   * this can never become "it is live"; it answers WHEN IS THIS EXPECTED and
   * nothing else. Optional by construction — a page sent for approval before a
   * date exists is the normal case, not an incomplete one.
   */
  occursOn?: string;
}

/** Cheap, honest URL check — http(s) and a host. Not a validity oracle. */
function isUsableUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') && Boolean(parsed.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * Assembling the packet.
 *
 * The event picker pulls that event's plan in as candidate rows rather than
 * asking the operator to rebuild it — the client is meant to receive the
 * run-of-show they already know, dates intact. Undrafted slots come through
 * too, as plan rows, because a review that silently drops a "client POV post — no
 * draft yet" row recreates the gap this surface exists to close.
 *
 * The two free-text asks are first-class, not an afterthought: a real plan
 * contains "no broad announcement unless he decides" and "capture one real
 * observation", and neither is an artifact to approve.
 */
export interface NewApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | null;
  /** Pre-selects an event when launched from that event's dock. */
  initialEventId?: string | null;
  /**
   * Pre-selects ONE post when launched from the post inspector. It is added to
   * the drafted-posts list if the list does not already carry it (a scheduled or
   * older post is not among the recent drafts), and with no event picked it is
   * the only row ticked. Callers must pass a stable object (memoise it).
   */
  initialPost?: ApprovalCandidate | null;
}

export const NewApprovalDialog: React.FC<NewApprovalDialogProps> = ({
  open,
  onOpenChange,
  workspaceId,
  initialEventId = null,
  initialPost = null,
}) => {
  const { events } = useReachEvents();
  const [eventId, setEventId] = useState<string | null>(initialEventId);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [extras, setExtras] = useState<ExtraAsk[]>([]);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  /*
   * The success state has two shapes now, and they are genuinely different
   * facts: a batch went INTO THE ROOM (nothing to copy — the people in it
   * already hold permanent links), or a one-off expiring link was minted
   * because the room is empty. Conflating them is what produced two links per
   * person in the first place.
   */
  const [sentToRoom, setSentToRoom] = useState<number | null>(null);
  // Options the operator chose to offer, per candidate. Kept out of the
  // candidate objects themselves so a refetch of the plan never drops them.
  const [optionsByKey, setOptionsByKey] = useState<Record<string, ApprovalSnapshotOption[]>>({});
  const [varyingKey, setVaryingKey] = useState<string | null>(null);

  const { data: candidateData, isLoading: candidatesLoading } = useApprovalCandidates(
    workspaceId,
    eventId,
  );
  /*
   * 🔴 NO DEFAULT IN THE DESTRUCTURING — IT CAUSED AN INFINITE RENDER LOOP.
   *
   * This read `const { data: draftCandidates = [] } = …`. Whenever the query
   * has no data — still loading, or disabled because `workspaceId` is null —
   * `data` is `undefined` and `= []` mints a BRAND NEW ARRAY on every render.
   * That new identity sits in the dependency array of the seeding effect below,
   * so the effect re-ran on every render, called `setSelected`, and re-rendered:
   * "Maximum update depth exceeded", forever, burning CPU on the operator's main
   * approvals page. This dialog is mounted unconditionally by
   * `ReachApprovalsPage`, so it looped even while CLOSED.
   *
   * TanStack Query returns a referentially stable value for unchanged data, so
   * depending on the raw query value is stable; the `?? []` moves to the point
   * of USE, where a fresh array is harmless.
   */
  const { data: draftCandidatesData } = useDraftCandidates(workspaceId);
  const draftCandidates = useMemo(
    () =>
      initialPost
        ? mergePreselectedCandidate(draftCandidatesData ?? EMPTY_DRAFT_CANDIDATES, initialPost)
        : (draftCandidatesData ?? EMPTY_DRAFT_CANDIDATES),
    [draftCandidatesData, initialPost],
  );
  const createRequest = useCreateApprovalRequest();
  // Who is already in this workspace's room. If anyone is, this batch goes to
  // them and no new reviewer is minted.
  const { data: deskMembers = [] } = useDeskMembers(workspaceId);
  const roomIsEmpty = deskMembers.length === 0;
  const generateVariations = useGenerateVariations();

  const candidates = candidateData?.candidates ?? [];
  // One lookup over both sources, so the submit path never has to know which
  // list a ticked row came from.
  const allCandidates = useMemo(
    () => [...(candidateData?.candidates ?? []), ...draftCandidates],
    [candidateData, draftCandidates],
  );
  const selectedEvent = events.find((event) => event.id === eventId) ?? null;
  const selectedCandidates = useMemo(
    () => allCandidates.filter((candidate) => selected.has(candidate.key)),
    [allCandidates, selected],
  );
  const { data: unchangedResends = [] } = useUnchangedResends(workspaceId, selectedCandidates);

  useEffect(() => {
    if (!open) return;
    setEventId(initialEventId);
    setIssuedToken(null);
  }, [open, initialEventId]);

  /*
   * 🔴 WITH NO EVENT, THE DRAFTS ARE THE REVIEW — so the ones ready to publish start ticked.
   *
   * This used to seed only from `candidateData.candidates`, which is the
   * EVENT's plan. Opening "New review" without picking an event therefore
   * produced a dialog with nothing selected, a submit button that stayed
   * disabled, and the only real content on the page sitting under a heading
   * that says "Off by default". On the real ITC Tax workspace that is 11
   * finished LinkedIn/Instagram posts — every one with copy and an image —
   * which is exactly the work worth sending and the reason 0 of 72 items on
   * that desk carry any media.
   *
   * The off-by-default rule is still right where it was reasoned: a review
   * built FROM AN EVENT must not quietly absorb unrelated drafts. That rule is
   * about an event's packet, so it applies when there is an event and not
   * otherwise.
   */
  useEffect(() => {
    if (eventId) {
      setSelected(
        new Set(
          (candidateData?.candidates ?? [])
            .filter((candidate) => candidate.hasDraft)
            .map((candidate) => candidate.key),
        ),
      );
      return;
    }
    if (initialPost) {
      setSelected(new Set([initialPost.key]));
      return;
    }
    setSelected(
      new Set(
        draftCandidates
          .filter((candidate) => isCandidateReadyToPublish(candidate))
          .map((candidate) => candidate.key),
      ),
    );
  }, [candidateData, draftCandidates, eventId, initialPost]);

  // Seed the title from the event, but never overwrite one the operator typed.
  const selectedEventName = selectedEvent?.name ?? null;
  useEffect(() => {
    if (!selectedEventName) return;
    setTitle((current) => (current.trim() ? current : `${selectedEventName} — for your review`));
  }, [selectedEventName]);

  const canSubmit = useMemo(() => {
    /*
     * An ask only COUNTS if `handleCreate` would actually send it. A page with
     * a title but no usable URL is dropped there, so counting it here would
     * enable Send and then silently ship a batch one item short of what the
     * operator can see on screen.
     */
    const askIsSendable = (extra: ExtraAsk): boolean =>
      Boolean(extra.title.trim()) && (extra.kind !== 'page' || isUsableUrl(extra.url ?? ''));
    const hasItems = selected.size > 0 || extras.some(askIsSendable);
    // A name is required only to BOOTSTRAP an empty room. With members present
    // the batch is addressed to the room, so asking for a name would be asking
    // the operator to name someone who is already there.
    return Boolean(
      workspaceId && title.trim() && hasItems && (!roomIsEmpty || reviewerName.trim()),
    );
  }, [workspaceId, title, reviewerName, selected, extras, roomIsEmpty]);

  /**
   * Offer the client a choice on ONE item. Not applied to the whole packet by
   * default: a review where every card asks the reviewer to compare versions
   * is slower to finish than one recommended draft per card, and finishing is
   * the whole point.
   */
  const addOptionsFor = async (key: string, text: string, platform: string | null) => {
    if (!text.trim()) return;
    setVaryingKey(key);
    try {
      const result = await generateVariations.mutateAsync({
        text,
        platform,
        brandKitId: workspaceId,
        projectId: candidateData?.projectId ?? null,
        count: 2,
      });
      if (result.variations.length === 0) {
        toast.error('No usable options came back. The original stands.');
        return;
      }
      setOptionsByKey((current) => ({ ...current, [key]: result.variations }));
      toast.success(
        result.groundedInBrandVoice
          ? `${result.variations.length} options, in this workspace's voice.`
          : `${result.variations.length} options. No brand voice is set for this workspace, so they are not voice-matched.`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not generate options.');
    } finally {
      setVaryingKey(null);
    }
  };

  const addExtra = (kind: AskKind) => {
    setExtras((current) => [
      ...current,
      { key: `${kind}-${Date.now()}-${current.length}`, kind, title: '', body: '' },
    ]);
  };

  const handleCreate = async () => {
    if (!workspaceId) return;
    const items: NewApprovalItemInput[] = [];

    for (const candidate of allCandidates) {
      if (!selected.has(candidate.key)) continue;
      items.push({
        itemKind: candidate.itemKind,
        title: candidate.title,
        body: candidate.body,
        occursOn: candidate.occursOn,
        occursLabel: candidate.occursLabel,
        socialPostId: candidate.socialPostId,
        projectTaskId: candidate.projectTaskId,
        snapshot: optionsByKey[candidate.key]?.length
          ? { ...candidate.snapshot, options: optionsByKey[candidate.key] }
          : candidate.snapshot,
      });
    }

    for (const extra of extras) {
      if (!extra.title.trim()) continue;

      /*
       * A page carries its URL in the snapshot, never a `project_task_id` —
       * `approval_items_pointer_shape` would reject one anyway, which is the
       * point of leaving `page` out of that constraint's allowlist.
       */
      if (extra.kind === 'page') {
        if (!isUsableUrl(extra.url ?? '')) continue;
        items.push({
          itemKind: 'page',
          title: extra.title.trim(),
          body: extra.body.trim() || null,
          /* Empty string is "not given", never today's date. A guessed date on
             a client-facing card is a claim Mora cannot support. */
          occursOn: extra.occursOn?.trim() || null,
          snapshot: snapshotFromExternalPage({
            url: (extra.url ?? '').trim(),
            title: extra.title.trim(),
            note: extra.body.trim() || null,
          }),
        });
        continue;
      }

      items.push({
        itemKind: extra.kind,
        title: extra.title.trim(),
        body: extra.body.trim() || null,
        snapshot: { body: extra.body.trim() || null },
      });
    }

    try {
      const created = await createRequest.mutateAsync({
        workspaceId,
        title: title.trim(),
        note: note.trim() || null,
        reachEventId: eventId,
        projectId: candidateData?.projectId ?? null,
        dueOn: dueOn || null,
        // Omitted when the room has members: sending into the room IS the
        // absence of this field. `resolveReviewContext` already scopes a desk
        // member to every non-draft request in their workspace, so the batch is
        // visible to them the moment it exists.
        reviewerName: roomIsEmpty ? reviewerName.trim() : undefined,
        reviewerEmail: roomIsEmpty ? reviewerEmail.trim() || null : undefined,
        items,
      });
      if (created.token) {
        setIssuedToken(created.token);
      } else {
        setSentToRoom(created.deskMemberCount);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not create this review. Try again.',
      );
    }
  };

  const shareLink = issuedToken ? shareLinkFor(window.location.origin, issuedToken) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] gap-0 overflow-y-auto sm:max-w-xl">
        {sentToRoom !== null ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {sentToRoom === 0 ? 'Saved, but nobody can see it' : 'It is in the room'}
              </DialogTitle>
              <DialogDescription>
                {sentToRoom === 0
                  ? 'This workspace has nobody in its content desk yet, so this batch is not addressed to anyone. Add a person below the reviews list and their link will show it.'
                  : `${sentToRoom} ${sentToRoom === 1 ? 'person' : 'people'} in this desk can see it now, through the link they already have. Nothing new to send.`}
              </DialogDescription>
            </DialogHeader>
            {sentToRoom > 0 ? (
              <ul className="mt-4 space-y-1.5">
                {deskMembers.map((member) => (
                  <li key={member.id} className="text-caption text-muted-foreground">
                    {member.name}
                  </li>
                ))}
              </ul>
            ) : null}
            <DialogFooter className="mt-6">
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : shareLink ? (
          <>
            <DialogHeader>
              <DialogTitle>Send this link</DialogTitle>
              <DialogDescription>
                It opens without a login and expires in 30 days. This is the only time it can be
                shown — reissue from the review if it goes missing.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Input readOnly value={shareLink} className="font-mono text-caption" />
                <Button
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(shareLink);
                    toast.success('Link copied');
                  }}
                >
                  <CopyIcon className="size-4" weight="regular" />
                  Copy
                </Button>
              </div>
              <div className="rounded-lg bg-muted/40 p-3">
                <p className={SECTION_LABEL}>Ready to send</p>
                <p className="mt-1.5 whitespace-pre-wrap text-caption leading-relaxed text-muted-foreground">
                  {`${reviewerName.split(' ')[0]} — here's ${title.trim()}. Tap through and hit approve, or tell me what to change. Takes a couple of minutes.\n\n${shareLink}`}
                </p>
                <Button
                  variant="flat"
                  size="sm"
                  className="mt-2 px-0"
                  onClick={() => {
                    void navigator.clipboard.writeText(
                      `${reviewerName.split(' ')[0]} — here's ${title.trim()}. Tap through and hit approve, or tell me what to change. Takes a couple of minutes.\n\n${shareLink}`,
                    );
                    toast.success('Message copied');
                  }}
                >
                  Copy the message
                </Button>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Send work for review</DialogTitle>
              <DialogDescription>
                Everything you need from them, in one link they can open on a phone.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="approval-event">Event</Label>
                  <Select
                    value={eventId ?? 'none'}
                    onValueChange={(value) => setEventId(value === 'none' ? null : value)}
                  >
                    <SelectTrigger id="approval-event">
                      <SelectValue placeholder="Not event-specific" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not event-specific</SelectItem>
                      {withoutArchivedEvents(events).map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="approval-due">Due by</Label>
                  <Input
                    id="approval-due"
                    type="date"
                    value={dueOn}
                    onChange={(e) => setDueOn(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="approval-title">Title</Label>
                <Input
                  id="approval-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Content for your review"
                />
              </div>

              {roomIsEmpty ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="approval-reviewer">Who is reviewing</Label>
                    <Input
                      id="approval-reviewer"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                      placeholder="Their first name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="approval-email">Their email (optional)</Label>
                    <Input
                      id="approval-email"
                      type="email"
                      value={reviewerEmail}
                      onChange={(e) => setReviewerEmail(e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                /*
                 * The room already has people, so there is nobody to name. This
                 * says who will see it instead — the question the operator is
                 * actually asking when they look for a reviewer field.
                 */
                <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
                  <p className={SECTION_LABEL}>Goes to</p>
                  <p className="mt-1 text-caption text-muted-foreground">
                    {deskMembers.map((member) => member.name).join(' · ')} — through the link they
                    already have. No new link is created.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="approval-note">A line to them</Label>
                <Textarea
                  id="approval-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="resize-none"
                  placeholder="Optional. A line to say what you need from them."
                />
              </div>

              {eventId ? (
                <div className="space-y-2">
                  <p className={SECTION_LABEL}>From this event&apos;s plan</p>
                  {candidatesLoading ? (
                    <p className="text-caption text-muted-foreground">Loading the plan…</p>
                  ) : candidates.length === 0 ? (
                    <p className="text-caption leading-relaxed text-muted-foreground">
                      This event has no plan yet. Plan it first, or add asks below.
                    </p>
                  ) : (
                    <div className="rounded-lg border border-border/50">
                      {candidates.map((candidate, index) => (
                        <label
                          key={candidate.key}
                          className={`flex cursor-pointer items-start gap-3 px-3 py-2.5 ${
                            index > 0 ? 'border-t border-border/30' : ''
                          }`}
                        >
                          <Checkbox
                            checked={selected.has(candidate.key)}
                            onCheckedChange={(checked) => {
                              setSelected((current) => {
                                const next = new Set(current);
                                if (checked) next.add(candidate.key);
                                else next.delete(candidate.key);
                                return next;
                              });
                            }}
                            className="mt-0.5"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-caption font-medium text-foreground">
                              {candidate.title}
                            </span>
                            <span className="block text-detail text-muted-foreground">
                              {candidate.hasDraft ? 'Drafted' : 'Planned — no draft yet'}
                              {formatEventDate(candidate.occursOn)
                                ? ` · ${formatEventDate(candidate.occursOn)}`
                                : ''}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              {draftCandidates.length > 0 ? (
                <div className="space-y-2">
                  <p className={SECTION_LABEL}>Drafted posts</p>
                  {/* The copy has to match what actually happened, or the first
                      thing the operator learns is that the page lies about its
                      own defaults. */}
                  <p className="text-detail leading-relaxed text-muted-foreground">
                    {eventId
                      ? 'Off by default — a review built from an event shouldn’t quietly pick up unrelated drafts.'
                      : 'Drafts that can publish are ticked. A draft with something to fix is left unticked. Tick or untick anything.'}
                  </p>
                  <div className="max-h-52 overflow-y-auto rounded-lg border border-border/50">
                    {draftCandidates.map((candidate, index) => (
                      <label
                        key={candidate.key}
                        className={`flex cursor-pointer items-start gap-3 px-3 py-2.5 ${
                          index > 0 ? 'border-t border-border/30' : ''
                        }`}
                      >
                        <Checkbox
                          checked={selected.has(candidate.key)}
                          onCheckedChange={(checked) => {
                            setSelected((current) => {
                              const next = new Set(current);
                              if (checked) next.add(candidate.key);
                              else next.delete(candidate.key);
                              return next;
                            });
                          }}
                          className="mt-0.5"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-caption font-medium text-foreground">
                            {candidate.title}
                          </span>
                          <span className="block text-detail text-muted-foreground">
                            {candidate.snapshot.platform
                              ? platformLabel(candidate.snapshot.platform)
                              : 'No platform'}
                            {candidate.snapshot.mediaUrls?.length ? ' · has media' : ''}
                            {optionsByKey[candidate.key]?.length
                              ? ` · ${optionsByKey[candidate.key].length} options`
                              : ''}
                            {isCandidateReadyToPublish(candidate) ? '' : ' · Not ready to publish'}
                          </span>
                        </span>
                        <Button
                          variant="flat"
                          size="sm"
                          className="shrink-0 px-2"
                          disabled={varyingKey !== null || !candidate.snapshot.body}
                          onClick={(e) => {
                            e.preventDefault();
                            void addOptionsFor(
                              candidate.key,
                              candidate.snapshot.body ?? '',
                              candidate.snapshot.platform ?? null,
                            );
                          }}
                        >
                          <SparkleIcon className="size-4" weight="regular" />
                          {varyingKey === candidate.key ? 'Working…' : 'Options'}
                        </Button>
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="space-y-2">
                <p className={SECTION_LABEL}>Anything else you need from them</p>
                {extras.map((extra, index) => (
                  <div key={extra.key} className="rounded-lg border border-border/50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-detail uppercase tracking-wider text-muted-foreground">
                        {extra.kind === 'decision'
                          ? 'A yes or no'
                          : extra.kind === 'page'
                            ? 'A page to review'
                            : 'Something from them'}
                      </span>
                      <Button
                        variant="flat"
                        size="sm"
                        onClick={() =>
                          setExtras((current) => current.filter((_, i) => i !== index))
                        }
                        aria-label="Remove this ask"
                      >
                        <TrashIcon className="size-4" weight="regular" />
                      </Button>
                    </div>
                    <Input
                      value={extra.title}
                      onChange={(e) =>
                        setExtras((current) =>
                          current.map((c, i) =>
                            i === index ? { ...c, title: e.target.value } : c,
                          ),
                        )
                      }
                      placeholder={
                        extra.kind === 'decision'
                          ? 'Do you want a broad company announcement?'
                          : extra.kind === 'page'
                            ? 'Property Tax Optimization & Portfolio Diagnostic'
                            : 'One real observation from a session'
                      }
                      className="mt-2"
                    />
                    {extra.kind === 'page' ? (
                      <>
                        <Input
                          type="url"
                          value={extra.url ?? ''}
                          onChange={(e) =>
                            setExtras((current) =>
                              current.map((c, i) =>
                                i === index ? { ...c, url: e.target.value } : c,
                              ),
                            )
                          }
                          placeholder="https://example.com/preview-page"
                          className="mt-2"
                        />
                        {/*
                          🔴 THE EXPECTED GO-LIVE DATE, AND IT IS THE ONLY WAY A
                          PAGE REACHES A CALENDAR. A `page` item carries no
                          `project_task_id` by construction (the
                          `approval_items_pointer_shape` constraint excludes it),
                          so `occurs_on` is its ONLY date. Measured 2026-09-09:
                          all 3 page items in production carried a URL and NONE
                          carried a date, because this field did not exist —
                          they could be approved and never scheduled.

                          Optional, and labelled so. A page is often sent for
                          approval before anyone has committed to a date, and an
                          empty value must read as a normal state rather than an
                          omission to chase.
                        */}
                        <div className="mt-2 space-y-1.5">
                          <Label className={FIELD_LABEL} htmlFor={`page-date-${extra.key}`}>
                            Expected live date (optional)
                          </Label>
                          <Input
                            id={`page-date-${extra.key}`}
                            type="date"
                            value={extra.occursOn ?? ''}
                            onChange={(e) =>
                              setExtras((current) =>
                                current.map((c, i) =>
                                  i === index ? { ...c, occursOn: e.target.value } : c,
                                ),
                              )
                            }
                          />
                        </div>
                        {/*
                          Stated once, here, where the operator is deciding to
                          send it — so nobody is surprised by the caveat the
                          client's card carries. Mora does not host or watch
                          this page and cannot tell when it changes.
                        */}
                        <p className="mt-2 text-detail text-muted-foreground">
                          They&apos;ll open this on your client&apos;s own site. Mora can&apos;t see
                          it change after you send it, and the card says so.
                        </p>
                      </>
                    ) : null}
                    <Textarea
                      value={extra.body}
                      onChange={(e) =>
                        setExtras((current) =>
                          current.map((c, i) => (i === index ? { ...c, body: e.target.value } : c)),
                        )
                      }
                      rows={2}
                      className="mt-2 resize-none"
                      placeholder="Any context that helps them answer."
                    />
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => addExtra('decision')}>
                    <PlusIcon className="size-4" weight="regular" />A yes or no
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addExtra('input')}>
                    <PlusIcon className="size-4" weight="regular" />
                    Something from them
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addExtra('page')}>
                    <PlusIcon className="size-4" weight="regular" />A page to review
                  </Button>
                </div>
              </div>
            </div>

            {unchangedResends.length > 0 ? (
              <div className="mt-5">
                <UnchangedResendNotice resends={unchangedResends} />
              </div>
            ) : null}

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleCreate()}
                disabled={!canSubmit || createRequest.isPending}
              >
                <CheckIcon className="size-4" weight="regular" />
                {createRequest.isPending
                  ? 'Sending…'
                  : roomIsEmpty
                    ? 'Create and get link'
                    : 'Send to the desk'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
